import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingBag, Plus, Minus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from "@/components/ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useCart } from "@/lib/cart-context";
import { useCountry } from "@/lib/country-context";
import { useAuth } from "@/lib/auth-context";
import { useToast } from "@/hooks/use-toast";
import api from "@/lib/api";

export function CartDrawer({ fullWidth = false }) {
  const { cart, updateQuantity, clearCart, totalItems, subtotal } = useCart();
  const { formatPrice } = useCountry();
  const { isCustomer } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [placing, setPlacing] = useState(false);

  const handleCheckout = async () => {
    if (!cart.restaurantId || cart.items.length === 0 || !address) return;
    if (!isCustomer) {
      toast({ title: "Sign in to place an order", variant: "destructive" });
      navigate("/signin");
      return;
    }
    setPlacing(true);
    try {
      const { data } = await api.post("/orders", {
        restaurant_id: cart.restaurantId,
        delivery_address: address,
        notes: notes || null,
        items: cart.items.map((i) => ({ menu_item_id: i.menuItemId, quantity: i.quantity })),
      });
      clearCart();
      setCheckoutOpen(false);
      setOpen(false);
      setAddress("");
      setNotes("");
      toast({ title: "Order placed!", description: "Your order is confirmed." });
      navigate(`/orders/${data.id}`);
    } catch (e) {
      toast({
        title: "Failed to place order",
        description: e?.response?.data?.detail || "Try again",
        variant: "destructive",
      });
    } finally {
      setPlacing(false);
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <Button
            variant={totalItems > 0 ? "default" : "outline"}
            className={`relative ${fullWidth ? "w-full justify-start" : ""}`}
            size={fullWidth ? "default" : "icon"}
            data-testid="cart-trigger-btn"
          >
            <ShoppingBag className={fullWidth ? "mr-2 h-4 w-4" : "h-5 w-5"} />
            {fullWidth && <span>Cart</span>}
            {totalItems > 0 && (
              <span className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                {totalItems}
              </span>
            )}
          </Button>
        </SheetTrigger>
        <SheetContent className="w-full sm:max-w-md flex flex-col bg-card border-l">
          <SheetHeader>
            <SheetTitle className="font-serif text-2xl">Your Order</SheetTitle>
            {cart.restaurantName && (
              <p className="text-sm text-muted-foreground">from {cart.restaurantName}</p>
            )}
          </SheetHeader>

          {cart.items.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground">
              <ShoppingBag className="h-16 w-16 mb-4 opacity-20" />
              <p className="font-medium">Your cart is empty</p>
              <p className="text-sm mt-2">Chat with Cravve to find something delicious.</p>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto py-4 space-y-3 no-scrollbar">
                {cart.items.map((item) => (
                  <div
                    key={item.menuItemId}
                    className="flex items-center gap-3 bg-muted/30 p-3 rounded-xl border"
                    data-testid={`cart-item-${item.menuItemId}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{item.name}</p>
                      <p className="text-primary font-semibold text-sm">{formatPrice(item.price)}</p>
                    </div>
                    <div className="flex items-center gap-2 bg-background border rounded-lg p-1">
                      <Button
                        variant="ghost" size="icon" className="h-6 w-6"
                        onClick={() => updateQuantity(item.menuItemId, item.quantity - 1)}
                        data-testid={`cart-decrease-${item.menuItemId}`}
                      >
                        <Minus className="h-3 w-3" />
                      </Button>
                      <span className="text-sm font-medium w-4 text-center">{item.quantity}</span>
                      <Button
                        variant="ghost" size="icon" className="h-6 w-6"
                        onClick={() => updateQuantity(item.menuItemId, item.quantity + 1)}
                        data-testid={`cart-increase-${item.menuItemId}`}
                      >
                        <Plus className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t pt-4 space-y-4">
                <div className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span data-testid="cart-subtotal">{formatPrice(subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Delivery</span>
                    <span>calculated at checkout</span>
                  </div>
                </div>
                <Button
                  className="w-full h-12 text-base font-semibold"
                  onClick={() => setCheckoutOpen(true)}
                  data-testid="cart-checkout-btn"
                >
                  Proceed to Checkout
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <Dialog open={checkoutOpen} onOpenChange={setCheckoutOpen}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Where are we delivering?</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-3">
            <div className="space-y-2">
              <Label htmlFor="addr">Delivery address</Label>
              <Input
                id="addr"
                placeholder="e.g. 12 Opebi Road, Lagos"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                data-testid="checkout-address-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Notes (optional)</Label>
              <Textarea
                id="notes"
                placeholder="Any special instructions?"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                data-testid="checkout-notes-input"
              />
            </div>
          </div>
          <Button
            className="w-full h-11 font-semibold"
            disabled={!address || placing}
            onClick={handleCheckout}
            data-testid="checkout-place-order-btn"
          >
            {placing ? "Placing order..." : `Place order — ${formatPrice(subtotal)}`}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
